import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import type { GuideClip as Clip } from "@/content/guides/clips";

// A guide's short looping clip (":::clip <id>", src/content/guides/clips.ts): a muted MP4 that plays only while it's
// on screen, with a Pause / Play button (it loops for longer than 5 seconds). Under prefers-reduced-motion it stays on
// its poster, the clip's last frame, until someone presses Play. The server renders the poster, so the page never
// waits for the video, and nothing is downloaded until the clip is about to be seen (preload="none").

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const GuideClip = ({ clip, alt }: { clip: Clip; alt: string }) => {
  const ref = useRef<HTMLVideoElement>(null);
  // null: not decided yet (SSR / before the first effect); true: the reader paused it or prefers reduced motion.
  const [paused, setPaused] = useState<boolean | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setPaused(prefersReducedMotion());
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || paused === null) return;
    if (visible && !paused) el.play().catch(() => setPaused(true));
    else el.pause();
  }, [visible, paused]);

  const playing = paused === false;
  return (
    <div className="relative">
      <video
        ref={ref}
        src={clip.src}
        poster={clip.poster.src}
        width={clip.poster.width}
        height={clip.poster.height}
        muted
        loop
        playsInline
        preload="none"
        aria-label={alt}
        className="block h-auto w-full bg-white"
        data-testid="guide-clip"
      />
      <button
        type="button"
        onClick={() => setPaused(playing)}
        aria-label={playing ? "Pause animation" : "Play animation"}
        className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-md backdrop-blur hover:bg-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hawk-orange"
      >
        {playing ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
      </button>
    </div>
  );
};

export default GuideClip;
